import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { useCanaryMe } from '../api/me/hooks';

/** Every canary page: a page of its own, outside the app's layout and sidebar */
export function CanaryShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { data: me } = useCanaryMe();

  const tabs = [
    { key: 'hub', to: '/canary', label: t('canary.tabs.hub', 'Overview'), active: pathname === '/canary' },
    { key: 'notes', to: '/canary/notes', label: t('canary.tabs.notes', 'Notes'), active: pathname.startsWith('/canary/notes') },
    { key: 'tasks', to: '/canary/tasks', label: t('canary.tabs.tasks', 'Tasks'), active: pathname.startsWith('/canary/tasks') },
    { key: 'stress', to: '/canary/stress', label: t('canary.tabs.stress', 'Stress test'), active: pathname.startsWith('/canary/stress') },
    ...(me?.isAdmin
      ? [{ key: 'admin', to: '/canary/admin', label: t('canary.tabs.admin', 'Admin'), active: pathname.startsWith('/canary/admin') }]
      : []),
  ];

  return (
    <div className="min-h-screen bg-background text-foreground" data-qa-id="canary-app">
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto flex h-14 items-center gap-6 px-6">
          <Link to="/canary" className="flex items-center gap-2 font-semibold" data-qa-id="canary-brand">
            <FlaskConical className="h-5 w-5 text-primary" />
            {t('canary.brand', 'Canary Conf')}
          </Link>
          <nav className="flex h-full gap-1" data-qa-id="canary-subnav">
            {tabs.map((tab) => (
              <Link
                key={tab.key}
                to={tab.to}
                data-qa-id={`canary-tab-${tab.key}`}
                className={cn(
                  'flex items-center border-b-2 px-3 text-sm font-medium transition-colors',
                  tab.active ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.label}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            {user && <span className="hidden text-sm text-muted-foreground sm:inline">{user.username}</span>}
            <Button asChild variant="outline" size="sm">
              <Link to="/" data-qa-id="canary-exit">
                <ArrowLeft className="mr-1 h-4 w-4" />
                {t('canary.exit', 'Back to the app')}
              </Link>
            </Button>
          </div>
        </div>
      </header>
      <main className="container mx-auto space-y-6 px-6 py-6">{children}</main>
    </div>
  );
}
